import React, { useState } from "react";
import { ArrowRight, Heart, Mail, Star, Trash2, Zap } from "lucide-react";
import generateAvatarUrl from "../../utils/generateAvatarUrl";
import { formatOutcome } from "../../utils/p2pHelpers";
import { isFavoriteTeamP2PActivity, isP2PInviteActivity } from "../../utils/activityNavigation";

function formatAgo(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 45) return "Just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 172800) return "Yesterday";
  return `${Math.floor(s / 86400)}d ago`;
}

const FollowingP2PActivityCard = ({
  item,
  unread,
  onMarkRead,
  onViewOffer,
  onDelete,
}) => {
  const md = item?.metadata || {};
  const isFavoriteTeam = isFavoriteTeamP2PActivity(item);
  const isInvite = isP2PInviteActivity(item);
  const creatorName = md.creatorName || "Someone you follow";
  const favoriteTeamName = md.favoriteTeamName || md.homeName || "Your team";
  const homeName = md.homeName || "Home";
  const awayName = md.awayName || "Away";
  const amount = Number(md.amount) || 0;
  const fixtureSnapshot = md.fixtureSnapshot || {};
  const outcomeText =
    md.outcomePreview ||
    formatOutcome(md.agreedOdds, fixtureSnapshot) ||
    "Open offer";

  const [avatarFailed, setAvatarFailed] = useState(false);

  return (
    <article
      className={`act-follow-p2p ${unread ? "act-follow-p2p-unread" : ""}`}
      onClick={() => unread && onMarkRead?.(item._id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && unread) onMarkRead?.(item._id);
      }}
      role="button"
      tabIndex={0}
    >
      <div className="act-follow-p2p-stripe" aria-hidden />

      <div className="act-follow-p2p-inner">
        <div className="act-follow-p2p-head">
          <span className="act-follow-p2p-badge">
            {isInvite ? (
              <>
                <Mail size={10} aria-hidden />
                P2P invite
              </>
            ) : isFavoriteTeam ? (
              <>
                <Star size={10} aria-hidden />
                Favorite team
              </>
            ) : (
              <>
                <Heart size={10} aria-hidden />
                Following
              </>
            )}
          </span>
          <span className="act-follow-p2p-time">{formatAgo(item.createdAt)}</span>
        </div>

        <div className="act-follow-p2p-hero">
          <div className="act-follow-p2p-avatar-wrap">
            {!avatarFailed ? (
              <img
                className="act-follow-p2p-avatar"
                src={generateAvatarUrl(creatorName)}
                alt=""
                width={42}
                height={42}
                onError={() => setAvatarFailed(true)}
              />
            ) : (
              <span className="act-follow-p2p-avatar act-follow-p2p-avatar-fallback">
                {creatorName.slice(0, 1).toUpperCase()}
              </span>
            )}
            <span className="act-follow-p2p-avatar-dot" aria-hidden>
              <Zap size={8} />
            </span>
          </div>

          <div className="act-follow-p2p-copy">
            <h3 className="act-follow-p2p-title">
              {isInvite ? (
                <>
                  <span className="act-follow-p2p-name">{creatorName}</span>
                  {" invited you to P2P"}
                </>
              ) : isFavoriteTeam ? (
                <>
                  <span className="act-follow-p2p-name">{favoriteTeamName}</span>
                  {" is on the P2P board"}
                </>
              ) : (
                <>
                  <span className="act-follow-p2p-name">{creatorName}</span>
                  {" posted a P2P offer"}
                </>
              )}
            </h3>
            <p className="act-follow-p2p-match">
              {isFavoriteTeam && !isInvite
                ? `${creatorName} · ${homeName} vs ${awayName}`
                : `${homeName} vs ${awayName}`}
            </p>
          </div>

          {unread ? <span className="act-row-dot" aria-label="Unread" /> : null}
        </div>

        <div className="act-follow-p2p-details">
          <div className="act-follow-p2p-outcome">
            <span className="act-follow-p2p-outcome-label">Backing</span>
            <span className="act-follow-p2p-outcome-text">{outcomeText}</span>
          </div>
          <div className="act-follow-p2p-stake">
            <span className="act-follow-p2p-stake-label">Stake</span>
            <strong className="tabular-nums">{amount.toLocaleString()}</strong>
          </div>
        </div>

        <button
          type="button"
          className="act-follow-p2p-cta"
          onClick={(e) => {
            e.stopPropagation();
            onViewOffer?.(item);
          }}
        >
          <span>{isInvite ? "Review invite" : "View offer"}</span>
          <ArrowRight size={12} aria-hidden />
        </button>
      </div>

      <button
        type="button"
        className="act-row-delete"
        aria-label="Remove activity"
        onClick={(e) => {
          e.stopPropagation();
          onDelete?.(item._id);
        }}
      >
        <Trash2 size={14} />
      </button>
    </article>
  );
};

export default FollowingP2PActivityCard;
